// Tub weight helpers for pick lists.
// Weight values are in grams unless otherwise noted.

export const DEFAULT_TUB_WEIGHT_CONFIG = Object.freeze({
	// Absolute limit used for all suggestions.
	// This is the total weight (tub tare + products).
	maxTubTotalWeightGrams: 25 * 1000,
	// Tare weight of the empty tub itself.
	// Using the heaviest known tub (2.15kg) as a safe default so total does not exceed 25kg.
	// Known tub weights:
	// - Big tub: 2.15kg
	// - Smaller tub: 1.8kg
	// - Big post tub: 0.8kg
	// - Small post tub: 1.5kg
	tubTareWeightGrams: 2150,
});

const toNonNegativeInt = (value) => {
	const numberValue = Number(value);
	if (!Number.isFinite(numberValue)) {
		return 0;
	}
	return Math.max(0, Math.round(numberValue));
};

export const resolveTubWeightConfig = () => {
	// Placeholder for tenant-configurable values.
	// Keeping this indirection so we can easily read from tenantSettings later.
	return DEFAULT_TUB_WEIGHT_CONFIG;
};

export const resolveTubWeightLimits = (config) => {
	const maxTotal = toNonNegativeInt(config?.maxTubTotalWeightGrams);
	const tareGrams = toNonNegativeInt(config?.tubTareWeightGrams);
	const maxProduct = Math.max(0, maxTotal - tareGrams);

	return {
		maxTubTotalWeightGrams: maxTotal,
		maxTubProductWeightGrams: maxProduct,
		tubTareWeightGrams: tareGrams,
	};
};

const createEmptyBreakdown = (totalProductGrams, limits) => ({
	tubeCount: 0,
	totalProductGrams,
	totalGrams: 0,
	averageProductGramsPerTub: 0,
	tubes: [],
	config: limits,
});

const buildBreakdownFromTubs = (tubes, totalProductGrams, limits) => {
	const tubeCount = tubes.length;
	const totalTareGrams = limits.tubTareWeightGrams * tubeCount;

	return {
		tubeCount,
		totalProductGrams,
		totalGrams: totalProductGrams + totalTareGrams,
		averageProductGramsPerTub: tubeCount ? (totalProductGrams / tubeCount) : 0,
		tubes,
		config: limits,
	};
};

const buildContinuousBreakdown = (totalProductGrams, limits) => {
	const maxProductPerTubGrams = limits.maxTubProductWeightGrams;
	const tareGrams = limits.tubTareWeightGrams;

	if (!totalProductGrams || !maxProductPerTubGrams) {
		return createEmptyBreakdown(totalProductGrams, limits);
	}

	const tubeCount = Math.max(1, Math.ceil(totalProductGrams / maxProductPerTubGrams));
	const baseProductPerTub = Math.floor(totalProductGrams / tubeCount);
	let remainderProductGrams = totalProductGrams % tubeCount;
	const tubes = [];

	for (let index = 1; index <= tubeCount; index += 1) {
		const productGrams = baseProductPerTub + (remainderProductGrams > 0 ? 1 : 0);
		const totalGrams = productGrams + tareGrams;
		tubes.push({
			index,
			productGrams,
			tareGrams,
			totalGrams,
			overMax: totalGrams > limits.maxTubTotalWeightGrams,
		});
		remainderProductGrams = Math.max(0, remainderProductGrams - 1);
	}

	return buildBreakdownFromTubs(tubes, totalProductGrams, limits);
};

const normaliseProductGroups = (productGroups) => {
	if (!Array.isArray(productGroups) || !productGroups.length) {
		return [];
	}

	const gramsByProductKey = productGroups.reduce((acc, productGroup, index) => {
		const productKey = productGroup?.productId || productGroup?.id || `group-${index}`;
		const productGrams = toNonNegativeInt(productGroup?.productGrams);

		if (!productGrams) {
			return acc;
		}

		acc[productKey] = (acc[productKey] || 0) + productGrams;
		return acc;
	}, {});

	return Object.keys(gramsByProductKey)
		.map(productKey => ({
			productKey,
			productGrams: gramsByProductKey[productKey],
		}))
		.sort((a, b) => b.productGrams - a.productGrams);
};

const assignToLightestTub = (tubes, productGroup) => {
	let lightestTubIndex = 0;
	for (let index = 1; index < tubes.length; index += 1) {
		if (tubes[index].productGrams < tubes[lightestTubIndex].productGrams) {
			lightestTubIndex = index;
		}
	}

	const selectedTub = tubes[lightestTubIndex];
	selectedTub.productGrams += productGroup.productGrams;
	selectedTub.productKeys.push(productGroup.productKey);
	return lightestTubIndex;
};

const tryPackIntoTubCount = ({
	productGroups,
	totalProductGrams,
	tubeCount,
	limits,
}) => {
	const maxProductPerTubGrams = limits.maxTubProductWeightGrams;
	const targetProductGramsPerTub = totalProductGrams / tubeCount;
	const tubes = Array.from({ length: tubeCount }, (_, index) => ({
		index: index + 1,
		productGrams: 0,
		tareGrams: limits.tubTareWeightGrams,
		totalGrams: limits.tubTareWeightGrams,
		productKeys: [],
		overMax: false,
	}));

	for (const productGroup of productGroups) {
		if (!maxProductPerTubGrams) {
			assignToLightestTub(tubes, productGroup);
			continue;
		}

		const canFitWithinLimit = productGroup.productGrams <= maxProductPerTubGrams;

		// If one product is heavier than max-per-tub by itself, keep it whole and place
		// it in the currently lightest tub. We'll mark that tub as over max later.
		if (!canFitWithinLimit) {
			assignToLightestTub(tubes, productGroup);
			continue;
		}

		let selectedTubIndex = -1;
		let bestScore = Number.POSITIVE_INFINITY;

		for (let index = 0; index < tubes.length; index += 1) {
			const candidateTub = tubes[index];
			const projectedProductGrams = candidateTub.productGrams + productGroup.productGrams;

			if (projectedProductGrams > maxProductPerTubGrams) {
				continue;
			}

			const distanceToTarget = Math.abs(targetProductGramsPerTub - projectedProductGrams);

			if (distanceToTarget < bestScore) {
				bestScore = distanceToTarget;
				selectedTubIndex = index;
				continue;
			}

			if (distanceToTarget === bestScore && selectedTubIndex > -1) {
				if (projectedProductGrams < tubes[selectedTubIndex].productGrams + productGroup.productGrams) {
					selectedTubIndex = index;
				}
			}
		}

		// No valid placement under max with this tub count.
		if (selectedTubIndex === -1) {
			return null;
		}

		tubes[selectedTubIndex].productGrams += productGroup.productGrams;
		tubes[selectedTubIndex].productKeys.push(productGroup.productKey);
	}

	const usedTubes = tubes.filter(tub => tub.productGrams > 0);

	return usedTubes.map((tub, index) => {
		const totalGrams = tub.productGrams + tub.tareGrams;
		return {
			index: index + 1,
			productGrams: tub.productGrams,
			tareGrams: tub.tareGrams,
			totalGrams,
			productKeys: tub.productKeys,
			overMax: totalGrams > limits.maxTubTotalWeightGrams,
		};
	});
};

const buildProductGroupBreakdown = (productGroupsInput, limits) => {
	const productGroups = normaliseProductGroups(productGroupsInput);
	const totalProductGrams = productGroups.reduce((acc, group) => acc + group.productGrams, 0);

	if (!totalProductGrams) {
		return createEmptyBreakdown(0, limits);
	}

	const maxProductPerTubGrams = limits.maxTubProductWeightGrams;
	if (!maxProductPerTubGrams) {
		const fallbackTubes = productGroups.map((productGroup, index) => {
			const totalGrams = productGroup.productGrams + limits.tubTareWeightGrams;
			return {
				index: index + 1,
				productGrams: productGroup.productGrams,
				tareGrams: limits.tubTareWeightGrams,
				totalGrams,
				productKeys: [productGroup.productKey],
				overMax: true,
			};
		});
		return buildBreakdownFromTubs(fallbackTubes, totalProductGrams, limits);
	}

	const minimumTubeCount = Math.max(1, Math.ceil(totalProductGrams / maxProductPerTubGrams));
	const maxTubeCount = Math.max(minimumTubeCount, productGroups.length);

	for (let tubeCount = minimumTubeCount; tubeCount <= maxTubeCount; tubeCount += 1) {
		const packedTubes = tryPackIntoTubCount({
			productGroups,
			totalProductGrams,
			tubeCount,
			limits,
		});

		if (packedTubes) {
			return buildBreakdownFromTubs(packedTubes, totalProductGrams, limits);
		}
	}

	// Fallback when constraints cannot be satisfied without splitting a product.
	// Keep each product in a single tub and mark over-max tubs accordingly.
	const fallbackTubes = productGroups.map((productGroup, index) => {
		const totalGrams = productGroup.productGrams + limits.tubTareWeightGrams;
		return {
			index: index + 1,
			productGrams: productGroup.productGrams,
			tareGrams: limits.tubTareWeightGrams,
			totalGrams,
			productKeys: [productGroup.productKey],
			overMax: totalGrams > limits.maxTubTotalWeightGrams,
		};
	});

	return buildBreakdownFromTubs(fallbackTubes, totalProductGrams, limits);
};

// Input can be:
// - number: total product grams (continuous/even split fallback)
// - array: [{ productId, productGrams }] to keep each product in a single tub.
export const buildSuggestedTubBreakdown = (input, config = DEFAULT_TUB_WEIGHT_CONFIG) => {
	const resolvedConfig = config || DEFAULT_TUB_WEIGHT_CONFIG;
	const limits = resolveTubWeightLimits(resolvedConfig);

	if (Array.isArray(input)) {
		return buildProductGroupBreakdown(input, limits);
	}

	const totalProductGrams = toNonNegativeInt(input);
	return buildContinuousBreakdown(totalProductGrams, limits);
};

export const formatKgFromGrams = (grams, { decimals = 1 } = {}) => {
	const resolvedGrams = Number.isFinite(Number(grams)) ? Number(grams) : 0;
	const kg = resolvedGrams / 1000;

	// Avoid noise like "15.0" for whole numbers.
	const fixed = kg.toFixed(decimals);
	return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};
